import { TLink } from "../../../types/link-types";
import { Link as GatsbyLink } from "gatsby-link";
import { cleanUrl, isOrHandleAsExternalUrl } from "../../../helpers";
import { MouseEvent } from "react";
import styles from "./link-wrapper.module.less";

type TLinkWrapperForCurrentPage = {
  onClick?: () => void;
  title?: string;
  className?: string;
  children?: React.ReactNode;
};

const LinkWrapperForCurrentPage = ({ onClick, title, className, children }: TLinkWrapperForCurrentPage) => {
  return (
    <span role="button" onClick={onClick} className={className}>
      {children ? children : title}
    </span>
  );
};

type TLinkWrapperProps = Partial<TLink> & {
  className?: string;
  children?: React.ReactNode;
  onClickCurrentPageLink?: () => void;
  currentPageUrl?: string;
  onPreNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void;
  enableExternalLinkIcon?: boolean;
};

const LinkWrapper = ({
  title,
  url,
  className,
  target,
  ariaLabel,
  rel,
  children,
  currentPageUrl,
  onClickCurrentPageLink,
  onPreNavigate,
  enableExternalLinkIcon,
}: TLinkWrapperProps) => {
  let hasBeenClicked = false;
  const onPreNavigateLocal = (event: MouseEvent<HTMLAnchorElement>) => {
    if (hasBeenClicked || typeof onPreNavigate !== "function") {
      return;
    }

    event.persist();
    event.preventDefault();
    onPreNavigate(event);
    hasBeenClicked = true;
    event.currentTarget.click();
  };

  if (!url) {
    return (
      <span className={className}>
        {children && children}
        {!children && title}
      </span>
    );
  }

  url = cleanUrl(url);

  if (currentPageUrl && currentPageUrl === url) {
    return (
      <LinkWrapperForCurrentPage
        className={className}
        onClick={onClickCurrentPageLink ? onClickCurrentPageLink : undefined}
        title={title}
      >
        {children && children}
        {!children && title}
      </LinkWrapperForCurrentPage>
    );
  }

  if (isOrHandleAsExternalUrl(url, target)) {
    return (
      <a
        onClick={onPreNavigateLocal}
        aria-label={ariaLabel}
        href={url}
        target={target}
        className={`${className} mb-0`}
        rel={rel}
      >
        {children && children}
        {!children && title}
        {enableExternalLinkIcon && <span className={`${styles.iconRight} ui-icon-arrow-outward`} />}
      </a>
    );
  }

  return (
    <GatsbyLink onClick={onPreNavigateLocal} aria-label={ariaLabel} className={className} to={url} rel={rel}>
      {children && children}
      {!children && title}
    </GatsbyLink>
  );
};

export default LinkWrapper;
